import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";

import { useFetchCompanyUserDataQuery } from "../api/usersApi";
import {
  useFetchStockCheckSessionQuery,
  useFetchStockCheckReportQuery,
  useScanStockCheckBarcodeMutation,
  useUndoStockCheckScanMutation,
  useCompleteStockCheckSessionMutation,
  useCancelStockCheckSessionMutation,
} from "../api/stockCheckApi";
import { getApiErrorMessage } from "../api/errorUtils";

import StockCheckScanInput   from "../components/StockCheck/StockCheckScanInput";
import StockCheckProgressBar from "../components/StockCheck/StockCheckProgressBar";
import StockCheckItemsTable  from "../components/StockCheck/StockCheckItemsTable";
import StockCheckReportCard  from "../components/StockCheck/StockCheckReportCard";

// ─── constants ───────────────────────────────────────────────────────────────

const MANAGER_ROLES = new Set(["admin", "owner", "manager"]);

const TABS = [
  { id: "ALL",       label: "All" },
  { id: "REMAINING", label: "Remaining" },
  { id: "COMPLETED", label: "Completed" },
  { id: "EXTRA",     label: "Extra" },
  { id: "UNKNOWN",   label: "Unknown" },
];

const FEEDBACK_CLASS = {
  incremented:    "success",
  completed:      "success",
  extra:          "warning",
  already_scanned:"info",
  unknown_barcode:"danger",
  undone:         "info",
  nothing_to_undo:"info",
};

const FEEDBACK_ICON = {
  incremented:     "tim-icons icon-check-2",
  completed:       "tim-icons icon-check-2",
  extra:           "tim-icons icon-simple-add",
  already_scanned: "tim-icons icon-refresh-02",
  unknown_barcode: "tim-icons icon-alert-circle-exc",
  undone:          "tim-icons icon-minimal-left",
  nothing_to_undo: "tim-icons icon-alert-circle-exc",
};

const FEEDBACK_LABEL = {
  incremented:     "Scanned",
  completed:       "Item complete",
  extra:           "Extra item",
  already_scanned: "Already scanned",
  unknown_barcode: "Unknown barcode",
  undone:          "Last scan undone",
  nothing_to_undo: "Nothing to undo",
};

const FEEDBACK_DURATION_MS = 3500;

const STATUS_COLOR = {
  ACTIVE:    "success",
  COMPLETED: "primary",
  CANCELLED: "secondary",
};

// ─── helpers ─────────────────────────────────────────────────────────────────

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      day:    "2-digit",
      month:  "short",
      year:   "numeric",
      hour:   "2-digit",
      minute: "2-digit",
    }).format(new Date(value));
  } catch (_) {
    return value;
  }
}

// ─── component ───────────────────────────────────────────────────────────────

function StockCheckStudio() {
  const { sessionId } = useParams();
  const numericId = Number(sessionId);

  // ── Auth / role ──────────────────────────────────────────────────────────
  const { data: companyUser } = useFetchCompanyUserDataQuery();
  const currentRole = String(companyUser?.user?.role || "").toLowerCase();
  const canManage   = MANAGER_ROLES.has(currentRole);

  // ── Polling: start at 5 s, stop once session is no longer ACTIVE ─────────
  const [pollMs, setPollMs] = useState(5000);

  const {
    data:      sessionData,
    isFetching: isLoadingSession,
    error:     sessionError,
  } = useFetchStockCheckSessionQuery(numericId, {
    skip:                    !numericId,
    pollingInterval:         pollMs,
    refetchOnMountOrArgChange: true,
  });

  //
  // Backend response shape for GET /stock-check/sessions/:id:
  //   { session, items, totalItems, totalExpectedUnits, totalScannedUnits }
  //
  const sessionMeta        = sessionData?.session;
  const items              = useMemo(() => sessionData?.items ?? [], [sessionData?.items]);
  const totalItems         = sessionData?.totalItems          ?? 0;
  const totalExpectedUnits = sessionData?.totalExpectedUnits  ?? 0;
  const totalScannedUnits  = sessionData?.totalScannedUnits   ?? 0;

  const isActive   = sessionMeta?.status === "ACTIVE";
  const isFinished = sessionMeta?.status === "COMPLETED" || sessionMeta?.status === "CANCELLED";

  // Stop polling once the session is closed
  useEffect(() => {
    const s = sessionMeta?.status;
    if (s === "COMPLETED" || s === "CANCELLED") {
      setPollMs(0);
    } else if (s === "ACTIVE") {
      setPollMs(5000);
    }
  }, [sessionMeta?.status]);

  const closedAt = sessionMeta?.completedAt || sessionMeta?.cancelledAt || null;

  // ── Report (manager-only, finished sessions) ─────────────────────────────
  const { data: report } = useFetchStockCheckReportQuery(numericId, {
    skip: !numericId || !isFinished || !canManage,
  });

  // ── Mutations ────────────────────────────────────────────────────────────
  const [scanBarcode,     { isLoading: isScanning }]   = useScanStockCheckBarcodeMutation();
  const [undoScan,        { isLoading: isUndoing }]    = useUndoStockCheckScanMutation();
  const [completeSession, { isLoading: isCompleting }] = useCompleteStockCheckSessionMutation();
  const [cancelSession,   { isLoading: isCancelling }] = useCancelStockCheckSessionMutation();

  // ── UI state ─────────────────────────────────────────────────────────────
  const [activeTab,    setActiveTab]    = useState("ALL");
  const [feedback,     setFeedback]     = useState(null);
  const [actionError,  setActionError]  = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);

  // ── Derived counts ───────────────────────────────────────────────────────
  const statusCounts = useMemo(
    () =>
      items.reduce(
        (acc, item) => {
          if (item.status === "COMPLETED")     acc.completed += 1;
          else if (item.status === "EXTRA")    acc.extra     += 1;
          else                                  acc.pending   += 1;
          return acc;
        },
        { completed: 0, pending: 0, extra: 0 }
      ),
    [items]
  );

  const unknownCount = isFinished
    ? (report?.summary?.unknownBarcodeCount ?? 0)
    : 0;

  const totals = { totalItems, totalExpectedUnits, totalScannedUnits };

  // ── Feedback auto-dismiss ─────────────────────────────────────────────────
  useEffect(() => {
    if (!feedback) return;
    const t = setTimeout(() => setFeedback(null), FEEDBACK_DURATION_MS);
    return () => clearTimeout(t);
  }, [feedback]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleScan = useCallback(
    async (barcode) => {
      setActionError(null);
      try {
        const result = await scanBarcode({ sessionId: numericId, barcode }).unwrap();
        setFeedback({ type: result?.result ?? "incremented", barcode });
      } catch (err) {
        const status = err?.status ?? err?.data?.statusCode;
        if (status === 409) {
          setFeedback({ type: "already_scanned", barcode });
        } else {
          setActionError(getApiErrorMessage(err, "Scan failed."));
        }
      }
    },
    [scanBarcode, numericId]
  );

  const handleUndo = useCallback(async () => {
    setActionError(null);
    try {
      const result = await undoScan(numericId).unwrap();
      setFeedback({ type: result?.result ?? "undone", barcode: result?.undoneBarcode ?? null });
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Undo failed."));
    }
  }, [undoScan, numericId]);

  const handleComplete = useCallback(async () => {
    setConfirmModal(null);
    setActionError(null);
    try {
      await completeSession(numericId).unwrap();
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Failed to complete session."));
    }
  }, [completeSession, numericId]);

  const handleCancel = useCallback(async () => {
    setConfirmModal(null);
    setActionError(null);
    try {
      await cancelSession(numericId).unwrap();
    } catch (err) {
      setActionError(getApiErrorMessage(err, "Failed to cancel session."));
    }
  }, [cancelSession, numericId]);

  // ── Render guards ─────────────────────────────────────────────────────────

  if (!numericId) {
    return (
      <div className="content sales-premium">
        <Alert color="danger">Invalid session ID.</Alert>
      </div>
    );
  }

  if (isLoadingSession && !sessionData) {
    return (
      <div className="content sales-premium" style={{ textAlign: "center", paddingTop: 48 }}>
        <Spinner color="primary" />
      </div>
    );
  }

  if (sessionError) {
    return (
      <div className="content sales-premium">
        <Alert color="danger">
          {getApiErrorMessage(sessionError, "Failed to load session.")}
        </Alert>
        <Link to="/admin/stock-check" className="btn btn-secondary btn-sm">
          ← Back to sessions
        </Link>
      </div>
    );
  }

  if (!sessionData) return null;

  // ── Main render ───────────────────────────────────────────────────────────

  return (
    <div className="content sales-premium">
      {/* Breadcrumb */}
      <nav className="sc-breadcrumb" aria-label="breadcrumb">
        <Link to="/admin/stock-check">← Stock Check</Link>
        <span className="sc-breadcrumb__sep">/</span>
        <span>
          Session #{sessionMeta?.id}
          {sessionMeta?.location?.name ? ` — ${sessionMeta.location.name}` : ""}
        </span>
        <Badge color={STATUS_COLOR[sessionMeta?.status] ?? "secondary"} pill>
          {sessionMeta?.status}
        </Badge>
      </nav>

      {/* Session info card */}
      <Card style={{ marginBottom: 16 }}>
        <CardBody>
          <Row>
            <Col xs={6} sm={4} className="mb-2">
              <div className="sc-meta-label">Location</div>
              <div className="sc-meta-value">{sessionMeta?.location?.name ?? "—"}</div>
            </Col>
            <Col xs={6} sm={4} className="mb-2">
              <div className="sc-meta-label">Started</div>
              <div className="sc-meta-value">{formatDate(sessionMeta?.startedAt)}</div>
            </Col>
            {closedAt && (
              <Col xs={6} sm={4} className="mb-2">
                <div className="sc-meta-label">Closed</div>
                <div className="sc-meta-value">{formatDate(closedAt)}</div>
              </Col>
            )}
            {sessionMeta?.note && (
              <Col xs={12} className="mt-1">
                <div className="sc-meta-label">Note</div>
                <div style={{ fontStyle: "italic" }}>{sessionMeta.note}</div>
              </Col>
            )}
          </Row>

          <div style={{ marginTop: 14 }}>
            <StockCheckProgressBar
              totals={totals}
              statusCounts={statusCounts}
              unknownCount={unknownCount}
              report={isFinished && canManage ? report : null}
            />
          </div>
        </CardBody>
      </Card>

      {/* Scan area — only when ACTIVE */}
      {isActive && (
        <Card style={{ marginBottom: 16 }}>
          <CardHeader>
            <h5 className="mb-0 sp-font-semibold">Scan Barcode</h5>
          </CardHeader>
          <CardBody>
            <StockCheckScanInput
              onScan={handleScan}
              isLoading={isScanning}
              disabled={!isActive}
            />

            {feedback && (
              <div className={`sc-feedback sc-feedback--${FEEDBACK_CLASS[feedback.type] ?? "info"}`}>
                <i className={`sc-feedback__icon ${FEEDBACK_ICON[feedback.type] ?? "tim-icons icon-bullet-list-67"}`} />
                <div>
                  <div className="sc-feedback__title">
                    {FEEDBACK_LABEL[feedback.type] ?? feedback.type}
                  </div>
                  {feedback.barcode && (
                    <div className="sc-feedback__sub">{feedback.barcode}</div>
                  )}
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {/* Action error */}
      {actionError && (
        <Alert color="danger" toggle={() => setActionError(null)} className="mb-3">
          {actionError}
        </Alert>
      )}

      {/* Action buttons */}
      {isActive && (
        <div className="sc-actions">
          <Button
            color="warning"
            size="sm"
            onClick={handleUndo}
            disabled={isUndoing}
          >
            {isUndoing ? <Spinner size="sm" /> : "↩ Undo Last Scan"}
          </Button>

          {canManage && (
            <>
              <Button
                color="success"
                size="sm"
                onClick={() => setConfirmModal("complete")}
                disabled={isCompleting || isCancelling}
              >
                ✓ Complete Session
              </Button>
              <Button
                color="danger"
                outline
                size="sm"
                onClick={() => setConfirmModal("cancel")}
                disabled={isCompleting || isCancelling}
              >
                ✕ Cancel Session
              </Button>
            </>
          )}
        </div>
      )}

      {/* Report or staff notice */}
      {isFinished && (
        <div style={{ marginBottom: 16 }}>
          {canManage ? (
            report ? (
              <>
                <div className="sp-label" style={{ marginBottom: 12 }}>Final Report</div>
                <StockCheckReportCard report={report} />
              </>
            ) : (
              <div className="text-center py-3">
                <Spinner size="sm" color="primary" />
                <span className="sp-text-muted" style={{ marginLeft: 8 }}>Loading report…</span>
              </div>
            )
          ) : (
            <Alert color="info">
              Session {sessionMeta?.status === "COMPLETED" ? "completed" : "cancelled"}.
              A manager can view the final report.
            </Alert>
          )}
        </div>
      )}

      {/* Items table with filter tabs */}
      <Card>
        <CardHeader style={{ paddingBottom: 0 }}>
          <div className="sc-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                className={`sc-tab${activeTab === tab.id ? " sc-tab--active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
                {tab.id === "REMAINING" && statusCounts.pending > 0 && (
                  <Badge color="primary" pill style={{ marginLeft: 5, fontSize: "0.7rem" }}>
                    {statusCounts.pending}
                  </Badge>
                )}
                {tab.id === "EXTRA" && statusCounts.extra > 0 && (
                  <Badge color="danger" pill style={{ marginLeft: 5, fontSize: "0.7rem" }}>
                    {statusCounts.extra}
                  </Badge>
                )}
                {tab.id === "UNKNOWN" && unknownCount > 0 && (
                  <Badge color="warning" pill style={{ marginLeft: 5, fontSize: "0.7rem" }}>
                    {unknownCount}
                  </Badge>
                )}
              </button>
            ))}
          </div>
        </CardHeader>

        <CardBody>
          <StockCheckItemsTable
            items={items}
            activeTab={activeTab}
            unknownBarcodes={isFinished && canManage ? (report?.unknownBarcodes ?? []) : []}
          />
        </CardBody>
      </Card>

      {/* Confirm modal */}
      <Modal isOpen={Boolean(confirmModal)} toggle={() => setConfirmModal(null)} centered size="sm">
        <ModalHeader toggle={() => setConfirmModal(null)}>
          {confirmModal === "complete" ? "Complete Session?" : "Cancel Session?"}
        </ModalHeader>
        <ModalBody>
          {confirmModal === "complete"
            ? "This will lock the session. No more scans can be added."
            : "This will cancel the session. All scan data will still be accessible."}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" size="sm" onClick={() => setConfirmModal(null)}>
            Go back
          </Button>
          {confirmModal === "complete" ? (
            <Button color="success" size="sm" onClick={handleComplete} disabled={isCompleting}>
              {isCompleting ? <Spinner size="sm" /> : "Complete"}
            </Button>
          ) : (
            <Button color="danger" size="sm" onClick={handleCancel} disabled={isCancelling}>
              {isCancelling ? <Spinner size="sm" /> : "Cancel Session"}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default StockCheckStudio;
